import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { useSite } from '@/contexts/SiteContext';
import { ArrowLeft, Globe, Eye, Search, FileText } from 'lucide-react';
import {
  competitiveLandscapeService,
  type ActorDetail,
  type ActorCategoryMetric,
} from '@/services/competitiveLandscapeService';

// ============================================================================
// Constants
// ============================================================================

const ROLE_COLORS: Record<string, string> = {
  competitor: 'bg-red-100 text-red-800',
  brand: 'bg-blue-100 text-blue-800',
  aggregator: 'bg-orange-100 text-orange-800',
  information: 'bg-green-100 text-green-800',
  marketplace: 'bg-purple-100 text-purple-800',
  partner: 'bg-teal-100 text-teal-800',
  channel: 'bg-cyan-100 text-cyan-800',
  influencer: 'bg-pink-100 text-pink-800',
};

const TYPE_COLORS: Record<string, string> = {
  ecommerce: 'bg-red-50 text-red-700',
  brand: 'bg-blue-50 text-blue-700',
  aggregator: 'bg-orange-50 text-orange-700',
  marketplace: 'bg-purple-50 text-purple-700',
  information: 'bg-green-50 text-green-700',
  forum: 'bg-emerald-50 text-emerald-700',
  review: 'bg-lime-50 text-lime-700',
  unknown: 'bg-gray-50 text-gray-500',
};

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'ChatGPT',
  anthropic: 'Claude',
  perplexity: 'Perplexity',
  google: 'Gemini',
};

// ============================================================================
// Component
// ============================================================================

export default function ActorProfile() {
  const { actorId } = useParams<{ actorId: string }>();
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;
  const { toast } = useToast();
  const navigate = useNavigate();

  const [actor, setActor] = useState<ActorDetail | null>(null);
  const [categories, setCategories] = useState<ActorCategoryMetric[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!siteId || !actorId) return;
    setLoading(true);
    try {
      const [actorData, categoryData] = await Promise.all([
        competitiveLandscapeService.getActorDetail(actorId, siteId),
        competitiveLandscapeService.getActorCategories(actorId, siteId, 30),
      ]);
      setActor(actorData);
      setCategories(categoryData.categories);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [siteId, actorId, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to view actor details.</AlertDescription></Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!actor) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Actor not found.</AlertDescription></Alert>
      </div>
    );
  }

  const sovMentions = actor.sovMetrics?.mention_count || 0;
  const sovQueries = actor.sovMetrics?.across_queries || 0;
  const sovProviders = actor.sovMetrics?.by_providers || [];
  const contentScores = actor.contentScores;

  return (
    <div className="p-6 space-y-6">
      {/* Back navigation */}
      <Button variant="ghost" onClick={() => navigate('/competitive-landscape')} className="gap-1 -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back to Landscape
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Globe className="h-6 w-6" />
            {actor.domain}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            {actor.name && <span className="text-muted-foreground">{actor.name}</span>}
            <Badge className={TYPE_COLORS[actor.site_type] || TYPE_COLORS.unknown}>
              {actor.site_type}
            </Badge>
            {actor.role && (
              <Badge className={ROLE_COLORS[actor.role] || ''}>
                {actor.role}
              </Badge>
            )}
            {actor.role_source === 'manual' && (
              <span className="text-xs text-muted-foreground">(manually set)</span>
            )}
          </div>
        </div>
        <div className="text-right text-sm text-muted-foreground">
          <div>First seen: {new Date(actor.first_seen_at).toLocaleDateString()}</div>
          <div>Last seen: {new Date(actor.last_seen_at).toLocaleDateString()}</div>
          {actor.classification_confidence && (
            <div>Classification confidence: {Math.round(actor.classification_confidence * 100)}%</div>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-1">
              <Eye className="h-3 w-3" /> SOV Mentions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sovMentions}</div>
            <div className="text-xs text-muted-foreground">across {sovQueries} queries</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-1">
              <Search className="h-3 w-3" /> AI Providers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {sovProviders.length > 0 ? sovProviders.map(p => (
                <Badge key={p} variant="outline" className="text-xs">
                  {PROVIDER_LABELS[p] || p}
                </Badge>
              )) : <span className="text-muted-foreground text-sm">None</span>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" /> Pages Analyzed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{contentScores?.pages_analyzed || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Categories Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Content analysis scores */}
      {contentScores && contentScores.pages_analyzed > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Content Analysis Scores (avg across {contentScores.pages_analyzed} pages)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm w-32">Content Depth</span>
              <Progress value={contentScores.avg_content_depth || 0} className="flex-1" />
              <span className="text-sm font-medium w-10 text-right">{contentScores.avg_content_depth || 0}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm w-32">Schema Coverage</span>
              <Progress value={contentScores.avg_schema_coverage || 0} className="flex-1" />
              <span className="text-sm font-medium w-10 text-right">{contentScores.avg_schema_coverage || 0}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm w-32">FAQ Coverage</span>
              <Progress value={contentScores.avg_faq_coverage || 0} className="flex-1" />
              <span className="text-sm font-medium w-10 text-right">{contentScores.avg_faq_coverage || 0}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Category Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {categories.length > 0 ? (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Composite</TableHead>
                    <TableHead className="text-center">SOV %</TableHead>
                    <TableHead className="text-center">Avg SERP</TableHead>
                    <TableHead className="text-center">Content</TableHead>
                    <TableHead className="text-center">Schema</TableHead>
                    <TableHead className="text-center">FAQs</TableHead>
                    <TableHead className="text-center">Providers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map(cat => (
                    <TableRow key={cat.category_path}>
                      <TableCell className="font-medium truncate max-w-[200px]">{cat.category_path}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={
                          cat.composite_authority_score >= 50 ? 'text-green-700 border-green-300' :
                          cat.composite_authority_score >= 25 ? 'text-yellow-700 border-yellow-300' :
                          'text-gray-500 border-gray-300'
                        }>
                          {Math.round(cat.composite_authority_score)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {cat.sov_pct ? `${cat.sov_pct.toFixed(1)}%` : '—'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {cat.avg_serp_position ? `#${cat.avg_serp_position.toFixed(1)}` : '—'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {cat.avg_content_depth_score ?? '—'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {cat.avg_schema_coverage_score ?? '—'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {cat.avg_faq_coverage_score ?? '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-1 justify-center">
                          {(cat.sov_providers || []).map(p => (
                            <Badge key={p} variant="outline" className="text-[10px] px-1 py-0">
                              {PROVIDER_LABELS[p]?.[0] || p[0]}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No category metrics available for this actor yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
